import { message } from 'antd'
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form'
import { userProfileAdminUser_user as UserProfileType } from '@Admin/AdminUserProfilePage/__generated__/userProfileAdminUser'

import {
    useDeleteAccountUserProfileMutation,
    useUpdateUserProfileMutation,
} from '~/gql/graphql'
import { SUCCESS, signOutUser } from '~/redux-modules/identity'
import { IdentityType } from '~/redux-modules/identity/type'
import { trackIdentify } from '~/lib/analytics/actions/identify'
import logger from '~/lib/logger'

import { FIRST_NAME, LAST_NAME, PHONE_NUMBER } from './constants'

export const handleDeleteAccount = async ({
    setLoading,
    identity,
    onCancel,
    deleteAccount,
    dispatch,
    handleAfterDelete,
}: {
    setLoading: (loading: boolean) => void
    identity: IdentityType | UserProfileType
    onCancel: () => void
    deleteAccount: ReturnType<typeof useDeleteAccountUserProfileMutation>[0]
    dispatch?: Function
    handleAfterDelete?: () => void
}) => {
    setLoading(true)
    try {
        await deleteAccount({
            variables: {
                id: identity.id,
            },
        })

        setLoading(false)
        onCancel()

        if (dispatch) {
            dispatch(signOutUser())
        }
        if (handleAfterDelete) {
            handleAfterDelete()
        }
    } catch (err) {
        message.error('Failed to delete account')
        setLoading(false)
        logger.exception(err, {
            extra: {
                info: 'MyAccountPage::Failed to delete account',
                identityId: identity.id,
            },
        })
    }
}

export const formatUserProfile = ({
    firstName,
    lastName,
    id,
    email,
    phoneNumber,
}: {
    id?: number
    email?: string | null
    firstName?: string | null
    lastName?: string | null
    phoneNumber?: string | null
}) => ({
    id: id,
    firstName: firstName,
    lastName: lastName,
    name: firstName && lastName ? `${firstName} ${lastName}` : email,
    phoneNumber: phoneNumber,
})

export const handleSubmit = ({
    form,
    setLoading,
    identity,
    dispatch,
    onCancel,
    updateUserProfile,
}: {
    form: WrappedFormUtils
    setLoading: Function
    identity: IdentityType
    dispatch: Function
    onCancel: () => void
    updateUserProfile: ReturnType<typeof useUpdateUserProfileMutation>[0]
}) => {
    if (!form.isFieldsTouched()) {
        return onCancel()
    }
    setLoading(true)
    form.validateFields(async (error, values) => {
        if (error) {
            return
        } else {
            try {
                const { data } = await updateUserProfile({
                    variables: {
                        id: identity.id,
                        profileInput: {
                            firstName: values[FIRST_NAME],
                            lastName: values[LAST_NAME],
                            phoneNumber: values[PHONE_NUMBER],
                        },
                    },
                })
                const newIdentity = Object.assign(
                    {},
                    identity,
                    formatUserProfile(data?.user?.editGeneralInfo || {}),
                )

                const action = { type: SUCCESS, identity: newIdentity }

                dispatch(
                    trackIdentify(action, newIdentity.id, newIdentity as any),
                )
                form.resetFields()
                setLoading(false)
                onCancel()
            } catch (err) {
                message.error('Failed to update user profile')
                setLoading(false)
                logger.exception(err, {
                    extra: {
                        info: 'MyAccountPage::Failed to update user profile',
                        identityId: identity.id,
                    },
                })
            }
        }
    })
}
